import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export const useLessonTutor = (open, lessonId) => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [messages, setMessages] = useState([]);
  const [chatId, setChatId] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [failedTurn, setFailedTurn] = useState(null);
  const [historyRetry, setHistoryRetry] = useState(0);
  const pending = useRef(null);
  const activeLesson = useRef(lessonId);
  const mounted = useRef(true);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    activeLesson.current = lessonId; pending.current = null;
    setMessages([]); setChatId(null); setSending(false); setError(''); setFailedTurn(null);
  }, [lessonId]);

  useEffect(() => {
    if (!open || !lessonId || pending.current?.lessonId === lessonId) return;
    const controller = new AbortController();
    setLoadingHistory(true); setError('');
    axios.get(`${API}/api/ai/lesson-chat/${lessonId}/history`, { headers, signal: controller.signal })
      .then(({ data }) => { setMessages(data.messages || []); setChatId(data.chat_id || null); setFailedTurn(null); })
      .catch(error => { if (!axios.isCancel(error)) setError('تعذر تحميل المحادثة السابقة. أعد المحاولة قبل المتابعة.'); })
      .finally(() => { if (!controller.signal.aborted) setLoadingHistory(false); });
    return () => controller.abort();
  }, [open, lessonId, headers, historyRetry]);

  const send = async (text, requestId = window.crypto.randomUUID()) => {
    if (!text.trim() || pending.current || loadingHistory) return false;
    const targetLesson = lessonId;
    pending.current = { requestId, lessonId: targetLesson };
    setSending(true); setError(''); setFailedTurn(null);
    setMessages(previous => previous.some(item => item.role === 'user' && item.request_id === requestId)
      ? previous.map(item => item.role === 'user' && item.request_id === requestId ? { ...item, status: 'pending' } : item)
      : [...previous, { role: 'user', content: text, request_id: requestId, status: 'pending' }]);
    try {
      const { data } = await axios.post(`${API}/api/ai/lesson-chat`,
        { lesson_id: targetLesson, message: text, request_id: requestId }, { headers, timeout: 180000 });
      if (!mounted.current || activeLesson.current !== targetLesson) return null;
      setChatId(data.chat_id);
      setMessages(previous => {
        const updated = previous.map(item => item.role === 'user' && item.request_id === requestId ? { ...item, status: 'sent' } : item);
        return updated.some(item => item.role === 'assistant' && item.request_id === requestId) ? updated
          : [...updated, { role: 'assistant', content: data.reply, request_id: requestId, phase: data.phase }];
      });
      return true;
    } catch (problem) {
      if (!mounted.current || activeLesson.current !== targetLesson) return null;
      const status = problem.response?.status;
      const detail = problem.response?.data?.detail;
      setError(status === 429 ? 'وصلت إلى الحد المؤقت للرسائل. انتظر قليلًا ثم أعد المحاولة.'
        : typeof detail === 'string' ? detail : 'تعذر إكمال الخطوة الآن. يمكنك إعادة المحاولة دون تكرار رسالتك.');
      setFailedTurn({ text, requestId });
      setMessages(previous => previous.map(item => item.role === 'user' && item.request_id === requestId ? { ...item, status: 'failed' } : item));
      return false;
    } finally {
      if (mounted.current && pending.current?.requestId === requestId) { pending.current = null; setSending(false); }
    }
  };

  const retry = () => failedTurn ? send(failedTurn.text, failedTurn.requestId) : setHistoryRetry(value => value + 1);
  return { messages, chatId, loadingHistory, sending, error, failedTurn, send, retry };
};